/**
 * ListTopWidgets — pasek kart podsumowania (widgetów) renderowany na SAMEJ GÓRZE
 * strony listy, nad zakładkami kategorii i paskiem filtrów.
 *
 * Decyzja Eugenii (2026-06-03): widgety mają być widoczne nad kategoriami na
 * KAŻDEJ liście — modułów systemowych i własnych. Wcześniej pasek montował się
 * wewnątrz `ConfigurableTable` (pod filtrami); teraz strona renderuje go u góry
 * przez ten komponent, a wewnętrzny pasek w `ConfigurableTable` jest usunięty.
 *
 * Samowystarczalny: czyta aktywny layout listy przez `useTableLayout(tableId)`
 * (ten sam cache co tabela — react-query dedupuje), sprawdza przełącznik
 * „pokaż statystyki" (`pageConfig.showStats`) i obecność definicji widgetów,
 * po czym renderuje `ListWidgetsBar`. Gdy wyłączone / brak widgetów → null
 * (zero miejsca w layoucie).
 *
 * `tableId`:
 *  - slug modułu systemowego (np. `contractors`) → stats z `/system-modules/:slug/stats`
 *  - `module-<id>` (moduł własny) → stats z `/modules/:id/stats`
 * Routing endpointu robi `ListWidgetsBar` po prefiksie.
 */

import { defaultPageConfig } from '@saas/shared';

import { ListWidgetsBar } from './ListWidgetsBar';
import type { TableId } from './types';
import { useTableLayout } from './useTableLayout';

interface ListTopWidgetsProps {
  /** Slug modułu systemowego lub `module-<id>` dla modułu własnego. */
  tableId: TableId;
  /** Opcjonalny UUID wybranej kategorii (kaskada wyboru layoutu — np. Kontrahenci). */
  categoryId?: string | null;
  /**
   * Firma listy — ta sama wartość `?companyId=`, którą strona wysyła do swojej listy (brak w
   * trybie „Wszystkie firmy"). Widgety liczą wtedy dokładnie rekordy widoczne na liście.
   */
  companyId?: string;
}

export function ListTopWidgets({
  tableId,
  categoryId,
  companyId,
}: ListTopWidgetsProps): React.ReactElement | null {
  const { layout, isLoading } = useTableLayout(tableId);

  if (isLoading) return null;

  const config = layout?.layoutConfig;
  const widgets = config?.widgets ?? [];

  // Domyślne ustawienia strony nakładamy gdy layout nie ma jeszcze `pageConfig`
  // (wsteczna zgodność — `defaultPageConfig.showStats === true`). Bez tego layout
  // zapisany tylko z zakładki „Widgety" (która nie dotyka `pageConfig`) miałby
  // `showStats` undefined → pasek nigdy by się nie pokazał mimo dodanych widgetów.
  const pageConfig = { ...defaultPageConfig, ...(config?.pageConfig ?? {}) };

  if (!pageConfig.showStats || widgets.length === 0) return null;

  return (
    <div data-testid="list-top-widgets">
      <ListWidgetsBar
        slug={tableId}
        widgets={widgets}
        categoryId={categoryId}
        companyId={companyId}
        widgetStyle={pageConfig.widgetStyle}
      />
    </div>
  );
}
